import {assertAndContinue, assert} from 'Asserts';
import {DataFrameColumnDef, DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {InputDataFrames, InputStepDataFramesMappedType, InputStepDatasRecord} from 'pipelines/StepDataUtils';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONObject, JSONValue} from 'util/Types';

import {TabbedUISchema} from './step-config-settings/StepConfigUISchemas';
// Use Symbols so that there's no possibility that a variable name and these keys conflict in a
// `ShapeUISchema` object.
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShapeV2
 */
export const FIELD_ORDER_UI_SCHEMA_KEY = Symbol('FieldOrderUISchemaKey');
/**
 * @see documenatation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const ON_CHANGE_CONFIG_MIDDLEWARE_KEY = Symbol('OnChangeConfigMiddlewareKey');

/**
 * Configuration for which fields trigger a backend call to regenerate step shape/config.
 *
 * When `triggerFields` is specified, only changes to those top-level config fields will fire a backend
 * request. The initial mount always fires regardless. If `triggerFields` is omitted or empty, the backend
 * is called on mount only — not on subsequent field changes.
 */
export interface BackendFieldGeneratorConfig {
  triggerFields?: ReadonlyArray<string>;
}

/**
 * Indicates that the step has backend field logic, and should call the backend step config API.
 * The backend is always called on mount. Subsequent calls are only made when a field listed in
 * `triggerFields` changes. If `triggerFields` is omitted or empty, the backend is only called on mount.
 */
export const BACKEND_FIELD_GENERATOR_CONFIG = Symbol('BackendFieldGeneratorConfig');

/**
 * Indicates that this step type has an expensive preview (e.g. LLM calls) and should not
 * auto-trigger preview on every config change. Instead, a "Generate Preview" button is shown.
 */
export const REQUIRES_MANUAL_PREVIEW = Symbol('RequiresManualPreview');

/**
 * @see documentation in
 * client/pipelines/generalized-step-config-editor/StepUISchemas.tsx _GetJsonSpecUISchemaShape
 */
export const CUSTOM_COMPONENT_KEY = Symbol('CustomComponent');

/**
 * Order the tabs in the tabbed container specified by the `TabbedUISchema`.
 */
export const TAB_ORDER_UI_SCHEMA_KEY = Symbol('TabOrderUISchemaKey');

/**
 * The name of the section to display in the tabbed container specified by the `TabbedUISchema`.
 */
export const SECTION_NAME_UI_SCHEMA_KEY = Symbol('SectionNameUISchemaKey');

/**
 * Whether to show a divider line between sections in the tabbed container specified by the
 * `ShapeUISchema`.
 */
export const SHOW_DIVIDER_LINE_KEY = Symbol('ShowDividerLineUISchemaKey');

/**
 * Allows showing a banner at the top of a V2 schema editor
 * `ShapeUISchema`.
 */
export const BANNER_KEY = Symbol('BannerKey');

export const CONDITIONAL_UI_SCHEMA_KEY = Symbol('ConditionalUISchemaKey');

/**
 * Allows info text for section names
 * `ShapeUISchema`.
 */
export const INFO_TEXT_UI_SCHEMA_KEY = Symbol('InfoTextUISchemaKey');

/**
 * Allows collapsible sections
 * `ShapeUISchema`.
 */
export const IS_COLLAPSIBLE_SCHEMA_KEY = Symbol('IsCollapsibleUISchemaKey');

/**
 * Allows expandable details for sections with header field and detail fields
 * `ShapeUISchema`.
 */
export const EXPANDABLE_DETAILS_KEY = Symbol('ExpandableDetailsUISchemaKey');

/**
 * When set on a section's UI schema, names the array field whose add icon should
 * appear inline in the section header. The default add button below the array items
 * is suppressed automatically for the named field (via `fieldSettings.hideDefaultAddButton`).
 * Only one field per section is supported.
 */
export const SECTION_ADD_ICON_FIELD_KEY = Symbol('SectionAddIconFieldUISchemaKey');

/**
 * Allows defining a function to generate default field values for new shape instances.
 * This is useful when defaults depend on runtime context like configuration state or data sources.
 * `ShapeUISchema`.
 */
export const FIELD_DEFAULTS_GENERATOR_KEY = Symbol('FieldDefaultsGeneratorUISchemaKey');

/**
 * Sets the number of columns to render components in. If not specified, defaults to 1 column.
 * `ShapeUISchema`
 */
export const NUM_COLUMNS_KEY = Symbol('NumColumnsUISchemaKey');

export interface ConfigDataFrameInfo {
  // We don't always have access to a persisted data frame, which is why id is optional. It's needed for any
  // editor components that require using the values of the data frame.
  id?: string;
  dataFrameSpec: DataFrameSpec;
}

export type ConfigDataFramesInfos<
  TStepType extends PipelineStepType,
  TStepVersion extends number,
> = InputStepDataFramesMappedType<TStepType, TStepVersion, Array<ConfigDataFrameInfo>>;

export function getInputDataFramesFromInfos<TStepType extends PipelineStepType, TStepVersion extends number>(
  dataFramesInfos: ConfigDataFramesInfos<TStepType, TStepVersion>
): InputDataFrames<TStepType, TStepVersion> {
  return Object.fromEntries(
    Object.entries(dataFramesInfos).map(
      ([stepDataName, dataFrameInfoList]: [string, Array<ConfigDataFrameInfo>]) => [
        stepDataName,
        dataFrameInfoList.map(({dataFrameSpec}) => dataFrameSpec),
      ]
    )
    // We can't narrow the key's type in `fromEntries`.
  ) as InputDataFrames<TStepType, TStepVersion>;
}

export function getDataFrameInfosFromInputDataFrames<
  TStepType extends PipelineStepType,
  TStepVersion extends number,
>(
  dataFramesInfos: InputStepDatasRecord<TStepType, TStepVersion, PipelineStepDataKind.DATASET>
): ConfigDataFramesInfos<TStepType, TStepVersion> {
  return Object.fromEntries(
    Object.entries(dataFramesInfos).map(
      ([stepDataName, dataFrameSpecList]: [
        string,
        Array<PipelineStepRawData<PipelineStepDataKind.DATASET>>,
      ]) =>
        [stepDataName, dataFrameSpecList.map(({id, ...dataFrameSpec}) => ({id, dataFrameSpec}))] as [
          string,
          Array<ConfigDataFrameInfo>,
        ]
    )
    // We can't narrow the key's type in `fromEntries`.
  ) as ConfigDataFramesInfos<TStepType, TStepVersion>;
}

export enum StepConfigEditorOverride {
  CHECKBOX = 'Checkbox',
  ICON_SELECTOR = 'IconSelector',
  SEGMENTED_ENUM_SELECTOR = 'SegmentedEnumSelector',
  STRING_LABEL = 'StringLabel',
  MAPPING_ARRAY = 'MappingArray',
  DATE_RANGE = 'DateRange',
  FILE_UPLOAD = 'FileUpload',
}

export const STEP_CONFIG_EDITOR_TO_VALID_KIND: {
  [key in StepConfigEditorOverride]: Array<PipelineConfigJsonSpec['kind']>;
} = {
  [StepConfigEditorOverride.CHECKBOX]: ['boolean'],
  [StepConfigEditorOverride.ICON_SELECTOR]: ['enum'],
  [StepConfigEditorOverride.SEGMENTED_ENUM_SELECTOR]: ['enum'],
  [StepConfigEditorOverride.STRING_LABEL]: ['string'],
  [StepConfigEditorOverride.MAPPING_ARRAY]: ['array'],
  [StepConfigEditorOverride.DATE_RANGE]: ['shape'],
  [StepConfigEditorOverride.FILE_UPLOAD]: ['string'],
} as const;

interface StringFieldSettings {
  useTextArea?: boolean;
  defaultValue?: string;
}

export interface FileUploadFieldSettings {
  fileNamePath: ConfigShapePath;
  accept?: string;
  maxFileSizeBytes?: number;
}

export function isFileUploadFieldSettings(
  fieldSettings: FieldSettings | FileUploadFieldSettings | undefined
): fieldSettings is FileUploadFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('fileNamePath');
}

interface EnumFieldSettings {
  displayNames?: Record<string, string>;
  defaultValue?: string | number;
  placeholder?: string;
  renderCyclingSelector?: boolean;
  iconNames?: Record<string, BenchlingIconName>;
  svgImagePaths?: Record<string, string>;
  /** Returns which enum choices should be disabled given the current config. */
  disabledChoicesFn?: (params: DisableFunctionParams) => ReadonlySet<string>;
  /** Tooltip shown on hover for disabled choices. */
  disabledChoicesTooltip?: string;
  /** Maps choice values to group header labels for visual grouping in the dropdown. */
  choiceGroups?: Record<string, string>;
}

interface TaggedUnionFieldSettingsBase {
  tagKeyDisplayNames?: Record<string, string>;
  renderCyclingSelector?: boolean;
}

/**
 * Extended tagged union field settings with support for configuring the UI of choice-specific fields.
 */
export interface TaggedUnionFieldSettings extends TaggedUnionFieldSettingsBase {
  placeholder?: string;
  infoText?: string;
  tagKeyDisplayName?: string;
  choiceFieldOrder?: ReadonlyArray<string>;
  choiceUISchema?: Record<string, FieldUISchemaWithStandardComponent>;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newValue: JSONValue,
    path: ConfigShapePath,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
}

interface CheckboxFieldSettings {
  checkboxLabel: string;
  defaultValue?: boolean;
}

/** @package */
export interface SegmentedEnumFieldSettings extends Pick<EnumFieldSettings, 'displayNames' | 'defaultValue'> {
  displayMode: 'icon' | 'text';
  iconNames?: Record<string, BenchlingIconName>;
  svgImagePaths?: Record<string, string>;
  allowNull?: boolean;
  nullIconName?: BenchlingIconName;
  nullDisplayName?: string;
}

interface ColumnReferenceFieldSettings {
  noColumnSelectedText?: string;
  allowSelectNull?: boolean;
  // A string with optional * wildcards to filter only columns with system names that match the selector
  systemNameWildcardSelector?: string;
  renderCyclingSelector?: boolean;
}

interface ColumnValueFieldSettings {
  noValueSelectedText?: string;
  renderCyclingSelector?: boolean;
}

interface TableReferenceFieldSettings {
  disabledColumn?: Array<string>;
  hiddenColumn?: Array<string>;
}

interface ArrayFieldSettings {
  disableAddRemove?: boolean;
  /**
   * Suppresses the default add button `ArrayEditor` renders below the array items. Set
   * automatically by `ShapeEditor` for the field named by `SECTION_ADD_ICON_FIELD_KEY`.
   */
  hideDefaultAddButton?: boolean;
}

export interface MappingArrayFieldSettings {
  /* Path to the column name that drives the mapping
   * e.g., ['chartConfig', 'parameters', 'seriesColumnName']
   */
  mappingSourcePath: ConfigShapePath;

  /* The field name in each array element that holds the mapped value
   * e.g., 'seriesValue'
   */
  mappingKey: string;

  // The display name to use for the no-series case
  noMappingDisplayName?: string;

  /* Whether to show the no-mapping editor
   * e.g., true
   */
  showNoMappingEditor?: boolean;

  /* The sentinel value to use for the no-mapping case
   * e.g., '__BENCHLING_NO_MAPPING_DEFAULT__'
   */
  noMappingSentinel?: string;
}

export interface RangeFieldSettings {
  /* The key in the shape that holds the start value. */
  startKey: string;

  /* The key in the shape that holds the end value. */
  endKey: string;
}

export interface ConfigValueCondition {
  tagValue: string;
  configShapePath: ConfigShapePath;
}

interface ExpandableDetailsConfigBase {
  /** When set, the expand toggle is only visible if the condition is met. */
  visibleWhen?: ConfigValueCondition | {conditionFn: (config: Record<string, JSONValue>) => boolean};
  /** Whether the section starts expanded. Defaults to `false` (collapsed). */
  defaultExpanded?: boolean;
}

export interface EditorExpandableDetailsConfig extends ExpandableDetailsConfigBase {
  headerMode: 'editor';
  /** The single field whose editor is lifted into the header row (always exactly one). */
  headerField: string;
}

export interface SummaryExpandableDetailsConfig extends ExpandableDetailsConfigBase {
  headerMode: 'summary';
  /** Fields summarized in the read-only header, in order. How each renders is derived from its config-shape kind. */
  headerFields: Array<string>;
}

/**
 * How an expandable section renders its header row.
 *  - `editor`: the header field's own editor is shown beside the toggle (chart-config sections). The single
 *    `headerField` is lifted out of the expanded body.
 *  - `summary`: a read-only, data-driven summary of the element is shown beside the toggle. Every field
 *    stays editable in the expanded body. `headerFields` lists which fields to summarize.
 */
export type ExpandableDetailsConfig = EditorExpandableDetailsConfig | SummaryExpandableDetailsConfig;

/**
 * Function that generates default field values for a shape based on runtime context.
 * Used when creating new instances of shapes (e.g., array elements) where defaults
 * depend on configuration state or external data.
 */
export type FieldDefaultsGeneratorFn = (
  config: JSONObject,
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  index?: number
) => JSONObject;

export type FieldSettings =
  | StringFieldSettings
  | EnumFieldSettings
  | TaggedUnionFieldSettingsBase
  | TaggedUnionFieldSettings
  | CheckboxFieldSettings
  | SegmentedEnumFieldSettings
  | ColumnReferenceFieldSettings
  | ColumnValueFieldSettings
  | TableReferenceFieldSettings
  | ArrayFieldSettings
  | MappingArrayFieldSettings
  | RangeFieldSettings
  | FileUploadFieldSettings;

export interface DisableFunctionParams {
  config: Record<string, JSONValue>;
  columns: Array<DataFrameColumnDef>;
}
export type DisableFunction = (params: DisableFunctionParams) => boolean;

/**
 * Path through configuration structure supporting both object keys (string) and array indices (number)
 * Examples:
 * - ['user', 'name'] → config.user.name
 * - ['items', 0, 'title'] → config.items[0].title
 * - ['groups', 1, 'members', 2] → config.groups[1].members[2]
 */
export type ConfigShapePath = Array<string | number>;

/** Schema of a scalar field.
 *
 * Keep in sync with `FieldUISchemaWithStandardComponent` (benchling/pipelines/step_type_library/manifests/types/ui_schema.yaml) on the back-end
 */
export interface FieldUISchemaWithStandardComponent {
  displayName?: string;
  editorOverride?: StepConfigEditorOverride;
  infoText?: string;
  configShapePath: ConfigShapePath;
  fieldSettings?: FieldSettings;
  disableFn?: DisableFunction;
  elementUISchema?: FieldUISchemaWithStandardComponent | ShapeUISchema;
  [BANNER_KEY]?: FieldUISchemaBanner;
}

export function assertTypeValidForEditorOverride(
  editorOverride: StepConfigEditorOverride,
  configShape: PipelineConfigJsonSpec
): void {
  assertAndContinue(
    STEP_CONFIG_EDITOR_TO_VALID_KIND[editorOverride].includes(configShape.kind),
    'Editor override does not match config shape kind'
  );
}

export interface CustomComponentEditorProps {
  config: Record<string, JSONValue>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  configShape: PipelineConfigJsonSpec;
}
interface FieldUISchemaWithCustomComponent {
  displayName?: string;
  infoText?: string;
  [CUSTOM_COMPONENT_KEY]: (props: CustomComponentEditorProps) => JSX.Element;
  disableFn?: DisableFunction;
}

export interface FieldUISchemaActionButton {
  /**
   * The key of the action button, it should be provided through `actions` in the `StepConfigEditorContext`.
   *
   * If the action isn't provided through the context, the button will be disabled.
   */
  key: string;
  buttonText: string;
  buttonIcon?: string;
  buttonVariant?: 'primary' | 'secondary';
  helpText?: string;
}

export interface FieldUISchemaMarkdown {
  markdown: string;
}

export interface FieldUISchemaBanner {
  text: string;
  bannerType?: InlineBannerType;
  supportingInfo?: string;
  visibilityFn?: (config: Record<string, JSONValue>) => boolean;
  placement?: 'above' | 'below';
  initialShowMore?: boolean;
}

interface ShapeUISchemaSharedFields {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ReadonlyArray<string>;
  [SECTION_NAME_UI_SCHEMA_KEY]?: string;
  [SECTION_ADD_ICON_FIELD_KEY]?: string;
  [SHOW_DIVIDER_LINE_KEY]?: boolean;
  [CONDITIONAL_UI_SCHEMA_KEY]?:
    | ConfigValueCondition
    | {conditionFn: (config: Record<string, JSONValue>) => boolean};
  [INFO_TEXT_UI_SCHEMA_KEY]?: string;
  [IS_COLLAPSIBLE_SCHEMA_KEY]?: boolean;
  [EXPANDABLE_DETAILS_KEY]?: ExpandableDetailsConfig;
  [NUM_COLUMNS_KEY]?: number;
  [BANNER_KEY]?: FieldUISchemaBanner;
  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newValue: JSONValue,
    path: ConfigShapePath,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
  [BACKEND_FIELD_GENERATOR_CONFIG]?: BackendFieldGeneratorConfig;
  [REQUIRES_MANUAL_PREVIEW]?: boolean;
  [FIELD_DEFAULTS_GENERATOR_KEY]?: FieldDefaultsGeneratorFn;
}

/** @package */
export type ShapeUISchemaFieldComponentValues = ShapeUISchemaSharedFields & {
  [key: string]:
    | FieldUISchemaWithStandardComponent
    | FieldUISchemaWithCustomComponent
    | FieldUISchemaActionButton
    | FieldUISchemaMarkdown;
};

/** @package */
export type ShapeUISchemaNestedShapeValues = ShapeUISchemaSharedFields & {
  [key: string]: ShapeUISchema;
};

export type ShapeUISchema = ShapeUISchemaFieldComponentValues | ShapeUISchemaNestedShapeValues;

/** @package */
export interface EnumUISchema {
  displayNames?: Record<string | number, string>;
  infoText?: string;
  defaultValue?: string | number;
  placeholder?: string;
  renderCyclingSelector?: boolean;
  iconNames?: Record<string, BenchlingIconName>;
  svgImagePaths?: Record<string, string>;
  /** Maps choice values to group header labels for visual grouping in the dropdown. */
  choiceGroups?: Record<string, string>;
}

/** @package */
export interface TaggedUnionUISchema {
  placeholder?: string;
  infoText?: string;
  tagKeyDisplayNames?: Record<string, string>;
  renderCyclingSelector?: boolean;
  [FIELD_ORDER_UI_SCHEMA_KEY]?: ReadonlyArray<string>;

  [ON_CHANGE_CONFIG_MIDDLEWARE_KEY]?: (
    newConfig: JSONValue,
    oldConfig: JSONValue,
    dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>
  ) => JSONValue;
  [BACKEND_FIELD_GENERATOR_CONFIG]?: BackendFieldGeneratorConfig;
}

export type StepConfigUISchema =
  | ShapeUISchema
  | FieldUISchemaWithStandardComponent
  | FieldUISchemaWithCustomComponent
  | FieldUISchemaActionButton
  | FieldUISchemaMarkdown;

export type StepConfigUISchemaWithTabs = TabbedUISchema | StepConfigUISchema;

export function isShapeUISchema(schema: StepConfigUISchema): schema is ShapeUISchema {
  return schema.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaNestedShapeValues(
  schema: ShapeUISchema
): schema is ShapeUISchemaNestedShapeValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isShapeUISchemaFieldComponentValue(
  schema: ShapeUISchema
): schema is ShapeUISchemaFieldComponentValues {
  if (!schema[FIELD_ORDER_UI_SCHEMA_KEY]?.length) {
    return false;
  }
  assert(
    schema.hasOwnProperty(schema[FIELD_ORDER_UI_SCHEMA_KEY][0]),
    `Field ${schema[FIELD_ORDER_UI_SCHEMA_KEY][0]} is not defined in uiSchema`
  );
  const firstField = schema[schema[FIELD_ORDER_UI_SCHEMA_KEY][0]];
  return !firstField.hasOwnProperty(FIELD_ORDER_UI_SCHEMA_KEY);
}

export function isFieldUISchemaWithStandardComponent(
  schema: StepConfigUISchema
): schema is FieldUISchemaWithStandardComponent {
  return schema != null && schema.hasOwnProperty('configShapePath');
}

export function isTabbedUISchema(schema: StepConfigUISchemaWithTabs): schema is TabbedUISchema {
  return schema.hasOwnProperty(TAB_ORDER_UI_SCHEMA_KEY);
}

/**
 * Resolve the UI schema for a V2 field, handling both flat and section-based schemas.
 *
 * For most fields, returns the inner `fieldSettings` (e.g. `{defaultValue: ...}`).
 * For array fields, returns the full `FieldUISchemaWithStandardComponent` so callers
 * can access `elementUISchema` for recursive initial value generation.
 *
 * TODO(BNCH-148338): Consider splitting into separate helpers so the dual return type
 * is no longer needed for initial value generation.
 */
export function getFieldSettingsIfPresent(
  schema: ShapeUISchema | undefined,
  fieldName: string
): StepConfigUISchema | undefined {
  const field = schema?.[fieldName];
  if (field == null) {
    return undefined;
  }
  // Resolve the FieldUISchemaWithStandardComponent from either flat or section-based schema.
  // Flat V2: schema[fieldName] is the field directly.
  // Section-based V2: schema[fieldName][fieldName] is the field (section keys equal field names by convention).
  let resolvedField: FieldUISchemaWithStandardComponent | undefined;
  if (isFieldUISchemaWithStandardComponent(field as StepConfigUISchema)) {
    resolvedField = field as FieldUISchemaWithStandardComponent;
  } else if (field.hasOwnProperty(fieldName)) {
    const nestedField = (field as ShapeUISchema)[fieldName];
    if (isFieldUISchemaWithStandardComponent(nestedField as StepConfigUISchema)) {
      resolvedField = nestedField as FieldUISchemaWithStandardComponent;
    }
  }
  if (resolvedField == null) {
    return undefined;
  }
  // For array fields, return the full field schema so callers can access elementUISchema.
  // For other fields, return the inner fieldSettings (e.g. enum defaultValue).
  if (resolvedField.elementUISchema != null) {
    return resolvedField;
  }
  return resolvedField.fieldSettings != null
    ? (resolvedField.fieldSettings as StepConfigUISchema)
    : undefined;
}

export function isFieldUISchemaWithCustomComponent(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaWithCustomComponent {
  return field != null && field.hasOwnProperty(CUSTOM_COMPONENT_KEY);
}

export function isFieldUISchemaActionButton(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaActionButton {
  return field != null && field.hasOwnProperty('key');
}

export function isFieldUISchemaMarkdown(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaMarkdown {
  return field != null && field.hasOwnProperty('markdown');
}

export function isFieldUISchemaBanner(
  field: ShapeUISchema[keyof ShapeUISchema]
): field is FieldUISchemaBanner {
  return field != null && field.hasOwnProperty('text');
}

export function isStringFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is StringFieldSettings {
  // A string field's settings are identified by any of its known keys. Add new keys here as they're introduced.
  // `defaultValue` is shared with other field settings (e.g. enum, checkbox), so require it to be a string
  // to avoid matching those variants.
  if (fieldSettings == null) {
    return false;
  }
  return (
    fieldSettings.hasOwnProperty('useTextArea') ||
    typeof (fieldSettings as StringFieldSettings).defaultValue === 'string'
  );
}

export function isCheckboxFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is CheckboxFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('checkboxLabel');
}

export function isSegmentedEnumFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is SegmentedEnumFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('displayMode');
}

export function isColumnReferenceFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is ColumnReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('noColumnSelectedText') || fieldSettings.hasOwnProperty('allowSelectNull'))
  );
}

export function isColumnValueFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is ColumnValueFieldSettings {
  return fieldSettings != null && fieldSettings.hasOwnProperty('noValueSelectedText');
}

export function isTableReferenceFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is TableReferenceFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('disabledColumn') || fieldSettings.hasOwnProperty('hiddenColumn'))
  );
}

export function isArrayFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is ArrayFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('disableAddRemove') || fieldSettings.hasOwnProperty('hideDefaultAddButton'))
  );
}

export function isMappingArrayFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is MappingArrayFieldSettings {
  return (
    fieldSettings != null &&
    fieldSettings.hasOwnProperty('mappingSourcePath') &&
    fieldSettings.hasOwnProperty('mappingKey')
  );
}

export function isRangeFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is RangeFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('startKey') || fieldSettings.hasOwnProperty('endKey'))
  );
}

export function isTaggedUnionFieldSettings(
  fieldSettings: FieldSettings | undefined
): fieldSettings is TaggedUnionFieldSettings {
  return (
    fieldSettings != null &&
    (fieldSettings.hasOwnProperty('choiceUISchema') ||
      fieldSettings.hasOwnProperty('choiceFieldOrder') ||
      fieldSettings.hasOwnProperty('tagKeyDisplayName') ||
      fieldSettings.hasOwnProperty(ON_CHANGE_CONFIG_MIDDLEWARE_KEY))
  );
}
